import { computed, Directive, input } from '@angular/core';
import { cn } from './cn';

/**
 * Error state = `aria-invalid="true"` on the element (also the accessible signal). The
 * attribute variant outranks `border-input` by specificity. A `[class.border-destructive]`
 * binding on the host does NOT: it bypasses `cn()`/tailwind-merge, so both classes land on the
 * element and `.border-input` wins by coming later in the built CSS — the red border never
 * showed (EB-3809: add-billing / add-credit / regulation form). A static
 * `class="border-destructive"` would win, since `cn()` merges it over `border-input`.
 * @ticket EB-3809
 */
const INPUT_BASE =
  'flex w-full rounded-md border border-input bg-background text-sm text-foreground ring-offset-background placeholder:text-muted-foreground/70 focus-visible:outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/25 aria-[invalid=true]:border-destructive aria-[invalid=true]:focus-visible:ring-destructive/25 disabled:cursor-not-allowed disabled:opacity-60 transition-colors ' +
  // `flex` lays a date input's inner parts out as flex items, which leaves Chrome's calendar
  // icon right after the date text instead of at the right edge. Block restores the default.
  '[&[type=date]]:block';

@Directive({
  selector: 'input[hlmInput]',
  standalone: true,
  host: { '[class]': '_c()' },
})
export class HlmInputDirective {
  readonly userClass = input<string>('', { alias: 'class' });
  readonly size = input<'sm' | 'md' | 'lg'>('md');
  protected readonly _c = computed(() => {
    const sizes: Record<string, string> = {
      sm: 'h-8 px-2.5',
      md: 'h-10 px-3 py-2',
      lg: 'h-12 px-4',
    };
    return cn(INPUT_BASE, sizes[this.size()], this.userClass());
  });
}

@Directive({
  selector: 'textarea[hlmTextarea]',
  standalone: true,
  host: { '[class]': '_c()' },
})
export class HlmTextareaDirective {
  readonly userClass = input<string>('', { alias: 'class' });
  protected readonly _c = computed(() =>
    cn(INPUT_BASE, 'min-h-[90px] px-3 py-2 resize-y', this.userClass()),
  );
}

@Directive({
  selector: 'select[hlmNativeSelect]',
  standalone: true,
  host: { '[class]': '_c()' },
})
export class HlmNativeSelectDirective {
  readonly userClass = input<string>('', { alias: 'class' });
  protected readonly _c = computed(() =>
    cn(
      INPUT_BASE,
      'h-10 px-3 py-2 pr-8 appearance-none',
      this.userClass(),
    ),
  );
}
